import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { TOUR_STEPS, isTouchDevice, stepBody, stepEyebrow, stepTargets, stepTitle, visibleSteps, type TourControls, type TourStep } from "./tourSteps";
import "./FeatureTour.css";

/**
 * "Follow the Spark" first-use tour. It is LIVE: it opens with a welcome, then drives the real
 * app so the reader sees each feature actually happen. Gesture steps perform the real action (a
 * left swipe advances the card; swipe up / tap opens the story); demo steps open a real surface (a
 * "climate" search with results, the Brief, the menu). Every step offers a quiet Skip (and Escape
 * skips too): a tour you cannot leave is a wall, not a welcome. `onDone` fires once, on finish or
 * skip, and every surface is closed on the way out. House style: no em dashes in copy.
 */

const GESTURE_FALLBACK_MS = 3500;
const SWIPE_MIN = 34;
const TAP_MAX = 12;
const MARGIN = 14;

interface Rect { top: number; left: number; width: number; height: number }

function measure(target?: string): Rect | null {
  if (!target || typeof document === "undefined") return null;
  const el = document.querySelector(`[data-tour="${target}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

/** The rectangle that contains every lit control, used to place the tooltip and the hint. */
function unionRect(rects: Rect[]): Rect | null {
  if (!rects.length) return null;
  const top = Math.min(...rects.map((r) => r.top));
  const left = Math.min(...rects.map((r) => r.left));
  const bottom = Math.max(...rects.map((r) => r.top + r.height));
  const right = Math.max(...rects.map((r) => r.left + r.width));
  return { top, left, width: right - left, height: bottom - top };
}

/** The dimmed surround, as a set of rectangles that covers everything EXCEPT the lit controls.
 *  With one hole this is the familiar four panels. With several, the holes are cut out of one
 *  horizontal band, which is exactly the shape of a pair of arrows facing each other across a
 *  story. Anything that does not share the band is simply covered by the top/bottom panels. */
function blurPanels(holes: Rect[], pad: number): Array<React.CSSProperties> {
  const band = unionRect(holes);
  if (!band) return [];
  const top = band.top - pad;
  const bottom = band.top + band.height + pad;
  const panels: Array<React.CSSProperties> = [
    { top: 0, left: 0, width: "100%", height: Math.max(0, top) },
    { top: bottom, left: 0, width: "100%", bottom: 0 },
  ];
  const sorted = holes.slice().sort((a, b) => a.left - b.left);
  let x = 0;
  for (const h of sorted) {
    const gap = h.left - pad - x;
    if (gap > 0) panels.push({ top, left: x, width: gap, height: bottom - top });
    x = Math.max(x, h.left + h.width + pad);
  }
  panels.push({ top, left: x, right: 0, height: bottom - top });
  return panels;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

/** Keep the tooltip fully on-screen: centered for the welcome, below a small target if it fits,
 *  else above; large targets (the card) and demo steps pin above the bottom bar. */
function placeTooltip(rect: Rect | null, centered: boolean, vw: number, vh: number, ttW: number, ttH: number) {
  const left = clamp(rect ? rect.left + rect.width / 2 : vw / 2, MARGIN + ttW / 2, vw - MARGIN - ttW / 2);
  if (centered) return { top: clamp(vh / 2 - ttH / 2, MARGIN, vh - ttH - MARGIN), left: vw / 2 };
  const bottomPin = vh - ttH - MARGIN - 76;
  if (!rect) return { top: clamp(bottomPin, MARGIN, vh - ttH - MARGIN), left };
  const big = rect.height > vh * 0.46;
  if (big) return { top: clamp(bottomPin, MARGIN, vh - ttH - MARGIN), left };
  const below = rect.top + rect.height + MARGIN;
  if (below + ttH <= vh - MARGIN) return { top: below, left };
  const above = rect.top - MARGIN - ttH;
  if (above >= MARGIN) return { top: above, left };
  return { top: clamp(vh / 2 - ttH / 2, MARGIN, vh - ttH - MARGIN), left };
}

export default function FeatureTour({ onDone, controls, signedIn }: { onDone: () => void; controls: TourControls; signedIn: boolean }) {
  const touch = isTouchDevice();
  // Fixed at mount on purpose. The step list must not change shape underneath a reader who is
  // halfway through it (advancing a card can change whether the card in front of them has a
  // deep dive), or Back and the progress dots would point at different steps than they did a
  // moment ago.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const steps = useMemo(() => visibleSteps(TOUR_STEPS, { deepDive: controls.hasDeepDive(), phoneWeb: document.documentElement.classList.contains("phone-web") }), []);
  const [index, setIndex] = useState(0);
  // Every control this step lights, not just one. A pair of arrows facing each other across
  // the story is one control, and a spotlight on half of it teaches half of it.
  const [rects, setRects] = useState<Rect[]>([]);
  const rect = useMemo(() => unionRect(rects), [rects]);
  const [ttSize, setTtSize] = useState({ w: Math.min(320, 0.87 * (typeof window !== "undefined" ? window.innerWidth : 360)), h: 190 });
  const [fallbackReady, setFallbackReady] = useState(false);
  const [nailed, setNailed] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false); // demo steps: phase 1 = the panel is open
  const [finishing, setFinishing] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const performed = useRef(false);
  const controlsRef = useRef(controls);
  controlsRef.current = controls;

  const step: TourStep = steps[index];
  const last = index === steps.length - 1;
  // A member feature with no account behind it: the step points and explains, and never opens
  // the surface, because opening it would raise the sign-in gate on top of the tour.
  const locked = !!step.requiresAccount && !signedIn;
  const reduceMotion =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const resetSurfaces = useCallback(() => {
    const c = controlsRef.current;
    c.expand(false);
    c.showFull(false); // back to Key points, the reader's default view
    c.closeSearch();
    c.closeBrief();
    c.closeMenu();
    c.closeNav();
    c.closeVerify();
    c.closeDive();
  }, []);

  // On each step: close every surface and reset to "locate" phase (so Back/forward is clean).
  // Demo steps open their surface on reveal (when the reader taps the button), not on enter.
  useEffect(() => {
    performed.current = false;
    setRevealed(false);
    resetSurfaces();
  }, [index, resetSurfaces]);
  useEffect(() => () => resetSurfaces(), [resetSurfaces]);

  // Demo phase 2: open the real surface the step points at, with a live "climate" search.
  const reveal = useCallback(() => {
    if (locked) return; // a locked step never opens anything; its button just moves on
    setRevealed(true);
    if (navigator.vibrate) navigator.vibrate(10);
    const c = controlsRef.current;
    if (step.surface === "search") c.search("climate");
    else if (step.surface === "brief") c.openBrief();
    else if (step.surface === "menu") c.openMenu();
    else if (step.surface === "nav") c.revealNav();
    else if (step.surface === "full") c.showFull(true);
    else if (step.surface === "verify") c.openVerify();
    else if (step.surface === "dive") c.openDive();
  }, [step.surface, locked]);

  // Measure the spotlight target (spotlight steps only) on step change / resize / scroll.
  // The device target is tried first and the touch target is the fallback: a narrow desktop
  // window hides the toolbar arrows, and a step must never spotlight a control that is not there.
  const targets = stepTargets(step, touch);
  const targetKey = targets.join(",");
  useEffect(() => {
    const recompute = () => {
      const found = targetKey ? targetKey.split(",").map(measure).filter((r): r is Rect => !!r) : [];
      if (found.length) { setRects(found); return; }
      const fallback = measure(step.target);
      setRects(fallback ? [fallback] : []);
    };
    recompute();
    const raf = window.requestAnimationFrame(recompute);
    const t = window.setTimeout(recompute, 340); // after a card advance / panel open settles
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t);
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
    };
  }, [index, targetKey, step.target]);

  // The story's own arrows only appear when a mouse is over the reader, so during the tour
  // they are held open. Without this the spotlight would cut a hole around a control that is
  // sitting at opacity 0: a tour pointing at nothing.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("tour-live");
    return () => root.classList.remove("tour-live");
  }, []);

  useLayoutEffect(() => {
    if (tooltipRef.current) {
      const r = tooltipRef.current.getBoundingClientRect();
      setTtSize((s) => (Math.abs(s.h - r.height) > 1 || Math.abs(s.w - r.width) > 1 ? { w: r.width, h: r.height } : s));
    }
  }, [index, rect, nailed]);

  useEffect(() => {
    setFallbackReady(!step.gesture);
    if (!step.gesture) return;
    const t = window.setTimeout(() => setFallbackReady(true), GESTURE_FALLBACK_MS);
    return () => window.clearTimeout(t);
  }, [index, step.gesture]);

  // Leaving early is a legitimate outcome, from any step: close every surface the tour opened,
  // no confetti, report done. The reader lands back on the story they were reading, the exit is
  // instant and quiet, and they are never asked again.
  const skipTour = useCallback(() => {
    resetSurfaces();
    onDone();
  }, [onDone, resetSurfaces]);

  const toNext = useCallback(() => {
    if (last) {
      setFinishing(true);
      resetSurfaces();
      window.setTimeout(onDone, reduceMotion ? 0 : 900);
      return;
    }
    setNailed(null);
    setIndex((i) => Math.min(i + 1, steps.length - 1));
  }, [last, onDone, reduceMotion, resetSurfaces, steps.length]);

  // Perform the real action for a gesture step (once per step), so the demo actually happens.
  const runPerform = useCallback(() => {
    if (performed.current) return;
    performed.current = true;
    const c = controlsRef.current;
    if (step.perform === "nextCard") c.nextCard();
    else if (step.perform === "expand") c.expand(true);
  }, [step.perform]);

  const completeGesture = useCallback(() => {
    if (!step.gesture || nailed) return;
    if (navigator.vibrate) navigator.vibrate(12);
    runPerform();
    setNailed(step.nailed ?? "Nice");
    window.setTimeout(toNext, reduceMotion ? 0 : 720);
  }, [step.gesture, step.nailed, nailed, runPerform, toNext, reduceMotion]);

  // The primary button: demo steps open their surface first (locate -> reveal); gesture steps
  // perform their action first; then advance.
  const onPrimary = useCallback(() => {
    if (step.kind === "demo" && !revealed && !locked) { reveal(); return; }
    if (step.gesture) {
      runPerform();
      window.setTimeout(toNext, reduceMotion ? 0 : 360);
      return;
    }
    toNext();
  }, [step.kind, step.gesture, revealed, locked, reveal, runPerform, toNext, reduceMotion]);

  const goBack = useCallback(() => {
    setNailed(null);
    setIndex((i) => Math.max(i - 1, 0));
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); skipTour(); return; }
      if (step.gesture === "swipeX" && (e.key === "ArrowLeft" || e.key === "ArrowRight")) { e.preventDefault(); completeGesture(); return; }
      if (step.gesture === "open" && e.key === "ArrowUp") { e.preventDefault(); completeGesture(); return; }
      if (e.key === "Enter" || e.key === "ArrowRight") { e.preventDefault(); onPrimary(); }
      else if (e.key === "ArrowLeft" && index > 0) goBack();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step.gesture, index, completeGesture, onPrimary, goBack, skipTour]);

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    swipe.current = t ? { x: t.clientX, y: t.clientY } : null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const s = swipe.current;
    const t = e.changedTouches[0];
    swipe.current = null;
    if (!s || !t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (step.gesture === "swipeX" && Math.abs(dx) >= SWIPE_MIN && Math.abs(dx) > Math.abs(dy)) completeGesture();
    else if (step.gesture === "open" && ((dy < -SWIPE_MIN && Math.abs(dy) > Math.abs(dx)) || (Math.abs(dx) < TAP_MAX && Math.abs(dy) < TAP_MAX))) completeGesture();
  };
  const onClick = (e: React.MouseEvent) => {
    if (step.gesture === "open") { completeGesture(); return; }
    // Demo "locate" phase: tapping the highlighted button opens it (just like the real thing).
    if (step.kind === "demo" && !revealed && !locked && rects.length) {
      const within = rects.some((r) =>
        e.clientX >= r.left - 16 && e.clientX <= r.left + r.width + 16 &&
        e.clientY >= r.top - 16 && e.clientY <= r.top + r.height + 16);
      if (within) reveal();
    }
  };

  const vw = typeof window !== "undefined" ? window.innerWidth : 390;
  const vh = typeof window !== "undefined" ? window.innerHeight : 780;
  const welcome = step.kind === "welcome";
  const showSpotlight = (step.kind === "spotlight" || (step.kind === "demo" && !revealed)) && !!rect;
  const place = placeTooltip(showSpotlight ? rect : null, welcome, vw, vh, ttSize.w, ttSize.h);
  const pad = 8;
  const doingGesture = !!step.gesture && !fallbackReady;
  const bodyText = step.kind === "demo" && revealed && step.revealBody ? step.revealBody : stepBody(step, touch, locked);
  const primaryLabel =
    step.kind === "demo" && !revealed && !locked ? (step.openLabel ?? "Open")
      : welcome ? (step.cta ?? "Show me around")
        : last ? (step.cta ?? "Got it")
          : "Next";

  return (
    <div className={`tour-overlay${finishing ? " finishing" : ""}${welcome ? " welcome" : ""}${step.kind === "demo" ? " demo" : ""}`} role="dialog" aria-modal="true" aria-label="How to use InSnip">
      {welcome ? <div className="tour-backdrop" /> : null}

      {/* Blurred + dimmed surround built from four panels, leaving the target sharp in the middle. */}
      {showSpotlight ? (
        <>
          {blurPanels(rects, pad).map((s, i) => <div key={i} className="tour-blur-panel" style={s} />)}
          {rects.map((r, i) => (
            <div key={i} className="tour-ring" style={{ top: r.top - pad, left: r.left - pad, width: r.width + pad * 2, height: r.height + pad * 2 }} />
          ))}
        </>
      ) : null}

      <div className="tour-catch" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} onClick={onClick} />

      {/* One lit control gets a hint on top of it. A PAIR of lit controls does its own
          pointing, and a floating arrow between them would land in the middle of the story. */}
      {showSpotlight && rects.length < 2 ? (
        <div className={`tour-hint hint-${step.hint}`} style={{ top: rect!.top + rect!.height / 2, left: clamp(rect!.left + rect!.width / 2, 44, vw - 44) }} aria-hidden="true">
          {step.hint === "swipeX" ? <span className="hint-swipe-x">&larr;</span> : null}
          {step.hint === "open" ? <span className="hint-swipe-up">&uarr;</span> : null}
          {step.hint === "tap" ? <span className="hint-ring" /> : null}
        </div>
      ) : null}

      <div ref={tooltipRef} className={`tour-tooltip${welcome ? " centered" : ""}`} style={{ top: place.top, left: place.left, transform: "translateX(-50%)" }} onClick={(e) => e.stopPropagation()}>
        {/* ONE Skip, not two. Both of these were rendered at once: the tooltip carried a Skip
            above the progress dots AND another below the body, which is why the card looked
            like it had two exits. The one that stayed is the one at the foot of the card,
            beside Back and Next, where a reader looks for it. */}
        {welcome ? <div className="tour-welcome-spark" aria-hidden="true">&#10024;</div> : null}
        {!welcome ? (
          <div className="tour-progress" aria-hidden="true">
            {steps.map((_, i) => <span key={i} className={`tour-node${i < index ? " done" : ""}${i === index ? " active" : ""}`} />)}
          </div>
        ) : null}
        <span className="tour-eyebrow"><span className="tour-spark-dot" aria-hidden="true" /> {stepEyebrow(step, touch)}</span>
        <h3 className="tour-title">{stepTitle(step, touch)}</h3>
        <p className="tour-body" aria-live="polite">{bodyText}</p>

        {nailed ? <div className="tour-nailed">{nailed}</div> : null}

        {/* An always-available exit. A walkthrough a reader cannot leave is a trap, and this one
            sits over the stories they actually came for. Leaving counts as done, so it never
            reopens and nags. */}
        {/* "Skip", not "Skip the tour": the button shares the top row with the step count
            (.tour-count sits 52px in from the same edge), and the longer label sat on top of
            it. The full sentence lives in the aria-label. */}
        {!last ? (
          <button type="button" className="tour-skip" onClick={skipTour} aria-label="Skip the tour">
            Skip
          </button>
        ) : null}
        <div className="tour-actions">
          {index > 0 ? <button type="button" className="tour-back" onClick={goBack}>Back</button> : <span />}
          {doingGesture ? (
            <span className="tour-do-hint">{touch ? (step.gesture === "swipeX" ? "Try a swipe" : "Try it: swipe up or tap") : (step.gesture === "swipeX" ? "Press an arrow key" : "Try it")}</span>
          ) : (
            <button type="button" className="tour-next" onClick={onPrimary}>{primaryLabel}</button>
          )}
        </div>
        {!welcome ? <span className="tour-count" aria-hidden="true">{index} / {steps.length - 1}</span> : null}
      </div>

      {finishing && !reduceMotion ? (
        <div className="tour-burst" aria-hidden="true">
          {Array.from({ length: 14 }, (_, i) => <span key={i} style={{ "--i": i } as React.CSSProperties} />)}
          <strong className="tour-burst-text">&#10024; You're all set</strong>
        </div>
      ) : null}
    </div>
  );
}
